import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/forgot_password', { email });
      setSent(true);
      if (r.data?.mock_reset_url) setDevUrl(r.data.mock_reset_url);
      toast.success('Check your inbox for the reset link');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0E11] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#181A20] border-r border-[#2B3139]">
        <Link to="/" className="flex items-center gap-2">
          <img src="/bitgonode-logo.png" alt="BITGONODE" className="h-20 w-auto object-contain" />
        </Link>
        <div>
          <div className="font-display text-white text-4xl font-bold tracking-tighter mb-4">Locked out?</div>
          <p className="text-[#B7BDC6] max-w-md leading-relaxed">
            Enter your email and we'll send a one-time link to set a new password. Link expires in 60 minutes.
          </p>
        </div>
        <div className="text-xs text-[#5E6673]">© 2026 BITGONODE — Demo Exchange</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1 text-[#848E9C] hover:text-white text-xs mb-4" data-testid="forgot-back-link">
            <ArrowLeft className="w-3 h-3" /> Back to login
          </Link>
          <h1 className="font-display text-white text-3xl font-bold tracking-tight mb-2">Forgot password</h1>
          <p className="text-[#848E9C] text-sm mb-8">
            No worries. Enter your email and we'll send you a reset link.
          </p>
          {!sent ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-[#B7BDC6] text-xs uppercase tracking-wider">Email</Label>
                <Input id="email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required
                  className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535] mt-1.5"
                  data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold h-11 rounded-sm"
                data-testid="forgot-submit-btn">
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          ) : (
            <div className="border border-[#0ECB81]/40 bg-[#0ECB81]/10 p-4 rounded-sm space-y-3" data-testid="forgot-sent-block">
              <div className="flex items-center gap-2 text-[#0ECB81]">
                <Mail className="w-4 h-4" />
                <div className="text-sm font-semibold">Check your inbox</div>
              </div>
              <p className="text-[#B7BDC6] text-sm">
                If an account exists for <span className="text-white font-mono">{email}</span>, we've sent a reset link.
                The link is valid for 60 minutes.
              </p>
              {devUrl && (
                <div className="border border-[#F0B90B]/40 bg-[#F0B90B]/10 p-3 rounded-sm text-[11px]" data-testid="forgot-dev-url">
                  <div className="text-[#F0B90B] font-mono uppercase font-semibold mb-1">Dev mode</div>
                  <div className="text-[#B7BDC6] mb-1">Sender domain isn't verified — use this link:</div>
                  <a href={devUrl} className="text-[#FCD535] font-mono break-all hover:underline">{devUrl}</a>
                </div>
              )}
              <div className="text-xs text-[#848E9C]">
                Didn't get it? <button onClick={() => { setSent(false); setDevUrl(''); }} className="text-[#FCD535] hover:underline" data-testid="forgot-retry-btn">Try another email</button>
              </div>
            </div>
          )}
          <div className="text-sm text-[#848E9C] mt-6 text-center">
            Remembered it? <Link to="/login" className="text-[#FCD535] hover:underline" data-testid="link-login-from-forgot">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
